import { useState, useEffect } from 'react';
import { X, Mail, Phone, MapPin, IdCard } from 'lucide-react';
import {
  getRenterDetail, getBookingById,
  type ImportedBooking,
} from '../api';
import BookingDetailModal from './BookingDetailModal';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { formatDate } from '../utils/dates';

interface Props {
  renterId: number;
  onClose: () => void;
}

export default function RenterDetailModal({ renterId, onClose }: Props) {
  const { settings } = useSettings();
  const [data, setData]       = useState<Awaited<ReturnType<typeof getRenterDetail>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [viewBooking, setViewBooking] = useState<ImportedBooking | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRenterDetail(renterId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Kunne ikke hente lejer'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [renterId]);

  const openBooking = async (id: number) => {
    try {
      const b = await getBookingById(id);
      setViewBooking(b);
    } catch { /* ignore */ }
  };

  const fmt = (n: number | null | undefined) => n != null ? formatNumber(n, settings.numberFormat) + ' DKK' : '—';

  return (
    <>
      <div className="fixed inset-0 z-50 bg-ink-900/50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-cream-50 rounded-xl shadow-card-md w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-ink-900">{data?.renter.name || 'Lejer'}</h2>
              {data && <p className="text-xs text-ink-500">Oprettet {formatDate(data.renter.created_at)} · {data.bookings.length} udlejninger</p>}
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-cream-100 text-ink-500"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {loading && <div className="text-center text-ink-500 py-8">Indlæser…</div>}
            {error && <div className="bg-rose-50 text-rose-700 border border-rose-200 rounded p-3 text-sm">{error}</div>}

            {data && (
              <>
                {/* Kontaktinfo */}
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-400 mb-2">Kontakt</h3>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    {data.renter.email && (
                      <div className="flex items-start gap-2">
                        <Mail size={14} className="text-ink-400 mt-0.5 flex-shrink-0" />
                        <a href={`mailto:${data.renter.email}`} className="text-sage-700 hover:underline truncate">{data.renter.email}</a>
                      </div>
                    )}
                    {data.renter.phone && (
                      <div className="flex items-start gap-2">
                        <Phone size={14} className="text-ink-400 mt-0.5 flex-shrink-0" />
                        <a href={`tel:${data.renter.phone}`} className="text-sage-700 hover:underline">{data.renter.phone}</a>
                      </div>
                    )}
                    {(data.renter.address || data.renter.city) && (
                      <div className="flex items-start gap-2 md:col-span-2">
                        <MapPin size={14} className="text-ink-400 mt-0.5 flex-shrink-0" />
                        <span className="text-ink-700">
                          {[data.renter.address, [data.renter.postal_code, data.renter.city].filter(Boolean).join(' '), data.renter.country].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                    {data.renter.drivers_license_number && (
                      <div className="flex items-start gap-2">
                        <IdCard size={14} className="text-ink-400 mt-0.5 flex-shrink-0" />
                        <span className="text-ink-700 font-mono text-xs">{data.renter.drivers_license_number}</span>
                      </div>
                    )}
                  </dl>
                  {data.renter.notes && (
                    <div className="mt-3 text-sm text-ink-700 bg-cream-100 border border-ink-100 rounded p-2">
                      <div className="text-xs text-ink-400 uppercase tracking-wide mb-1">Note</div>
                      {data.renter.notes}
                    </div>
                  )}
                </section>

                {/* Udlejningshistorik */}
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-400 mb-2">
                    Udlejninger ({data.bookings.length})
                  </h3>
                  {data.bookings.length === 0 ? (
                    <div className="text-sm text-ink-500 text-center py-4">Ingen udlejninger endnu.</div>
                  ) : (
                    <table className="data-table w-full text-sm">
                      <thead>
                        <tr>
                          <th className="text-left px-3 py-2">Periode</th>
                          <th className="text-left px-3 py-2">Bil</th>
                          <th className="text-left px-3 py-2">Faktura</th>
                          <th className="text-right px-3 py-2">Payout</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.bookings.map(b => (
                          <tr key={b.id} className="row-hover cursor-pointer" onClick={() => openBooking(b.id)}>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {formatDate(b.s)}{b.s !== b.e && ` → ${formatDate(b.e)}`}
                              <div className="text-[11px] text-ink-400">UID {b.uid}</div>
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{b.registration_number || '—'}</td>
                            <td className="px-3 py-2 text-xs text-ink-500">
                              {b.invoice_number || '—'}
                              {b.cancelled === 1 && <div className="text-rose-700 mt-0.5">Annulleret</div>}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                              {(b.cancelled === 1) ? fmt(b.cancellation_fee) : fmt(Number(b.payout))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      </div>

      {viewBooking && (
        <BookingDetailModal
          invoice={viewBooking}
          onClose={() => setViewBooking(null)}
          onSave={updated => setViewBooking(updated)}
        />
      )}
    </>
  );
}
